// Los planes de equipo como se los MUESTRA la app: nombre, precio por país, créditos y qué
// incluye — ADR-2026-10-27-planes-de-equipo-en-la-pantalla-de-planes y -2.
//
// Qué plan es de equipo y sus topes lo dice `planCapabilities` (`esPlanDeEquipo`,
// `topeDeUsuarios`, `topeDeMarcas`). Esto es sólo lo que se lee en pantalla, copiado de la
// landing publicada (`atlas-landing/landing_page/src/components/pricing/PlanesEquipos.tsx`):
// si allá cambia un texto, un precio o los créditos, cambia acá.
//
// Todavía no hay alta self-service para estos planes: como en la landing, los tres llevan a
// reservar una demo. Cuando Mati cargue los planes en Rebill y Stripe, el botón pasa a
// cobrar como el resto.

import { esPlanDeEquipo } from './planCapabilities';
import { paisFlags } from './checkoutPlans';

/** La agenda de la demo (la misma de la landing). */
export const LINK_DEMO_PLANES_DE_EQUIPO = 'https://calendar.app.google/gzWp9426Womve3559';

const norm = (plan?: string | null) => (plan ?? '').toString().trim().toLowerCase();

/** "Equipos" / "Agencias", o `null` si no es Equipos ni Agencias (Corporativo es el plan a medida). */
export function nombreDelPlanDeEquipo(plan?: string | null): 'Equipos' | 'Agencias' | null {
  const p = norm(plan);
  if (p === 'equipos') return 'Equipos';
  if (/^agencias\d+$/.test(p)) return 'Agencias';
  return null;
}

/** Créditos por mes: Equipos 1.000; Agencias N, (N+1) × 1.000 (agencias1 = 2.000 … agencias8 = 9.000). */
export function creditosDelPlanDeEquipo(plan?: string | null): number | null {
  const p = norm(plan);
  if (p === 'equipos') return 1000;
  const n = /^agencias(\d+)$/.exec(p);
  return n ? (Number(n[1]) + 1) * 1000 : null;
}

/** Créditos de cada escalón de Agencias, en orden: agencias1 = 2.000 … agencias8 = 9.000. */
export const CREDITOS_DE_AGENCIAS = [2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000] as const;

/** El escalón (0..7) de un plan de Agencias, o `null` si no es Agencias. */
export function escalonDeAgencias(plan?: string | null): number | null {
  const n = /^agencias(\d+)$/.exec(norm(plan));
  if (!n) return null;
  const i = Number(n[1]) - 1;
  return i >= 0 && i < CREDITOS_DE_AGENCIAS.length ? i : null;
}

/**
 * ¿La card muestra el plan que la empresa tiene? Agencias, sólo en el escalón de sus créditos:
 * como Ultra en los individuales, moverse a 9.000 no es "Tu plan" (review Greptile #211).
 */
export function esSuPlanDeEquipo(
  clave: 'equipos' | 'agencias' | 'corporativo',
  escalon: number,
  planActual: string | null,
): boolean {
  if (clave !== claveDelPlanDeEquipo(planActual)) return false;
  return clave !== 'agencias' || escalon === escalonDeAgencias(planActual);
}

type Agencias = [string, string, string, string, string, string, string, string];

/**
 * Precios por mes, IVA incluido, en la moneda de cada país — los mismos textos de la landing
 * (`PRECIOS`), con los puntos de miles ya puestos. Se muestran tal cual: pasarlos por
 * `formatPriceForPais` cambiaría USD 179 por "178.99".
 */
const PRECIOS: Record<'ARS' | 'CLP' | 'COP' | 'MXN' | 'PEN' | 'USD', { equipos: string; agencias: Agencias }> = {
  ARS: {
    equipos: '279.999',
    agencias: ['509.999', '769.999', '999.999', '1.239.999', '1.469.999', '1.699.999', '1.939.999', '2.169.999'],
  },
  CLP: {
    equipos: '178.999',
    agencias: ['328.999', '498.999', '648.999', '798.999', '948.999', '1.098.999', '1.248.999', '1.398.999'],
  },
  COP: {
    equipos: '649.999',
    agencias: ['1.199.999', '1.849.999', '2.399.999', '2.949.999', '3.499.999', '4.049.999', '4.599.999', '5.199.999'],
  },
  MXN: {
    equipos: '3.599',
    agencias: ['6.599', '9.999', '12.999', '15.999', '18.999', '21.999', '24.999', '27.999'],
  },
  PEN: {
    equipos: '649',
    agencias: ['1.199', '1.849', '2.399', '2.899', '3.449', '3.999', '4.549', '5.099'],
  },
  USD: {
    equipos: '179',
    agencias: ['329', '499', '649', '799', '949', '1.099', '1.249', '1.399'],
  },
};

function monedaDelPais(pais: string | null | undefined): keyof typeof PRECIOS {
  const f = paisFlags(pais);
  if (f.isArgentina) return 'ARS';
  if (f.isChile) return 'CLP';
  if (f.isColombia) return 'COP';
  if (f.isMexico) return 'MXN';
  if (f.isPeru) return 'PEN';
  return 'USD';
}

/** Con factura A se descuenta el IVA (21%): lo que le cuesta de verdad a una empresa argentina. */
function netoFacturaA(precio: string): string {
  return Math.round(Number(precio.replace(/\./g, '')) / 1.21).toLocaleString('es-AR');
}

/**
 * Precio de Equipos o de un escalón de Agencias, ya formateado como el resto del catálogo
 * (`$279.999 ARS`). En Argentina suma lo que cuesta neto con factura A, como la landing.
 */
export function precioDePlanDeEquipo(
  clave: 'equipos' | 'agencias',
  escalon: number,
  pais: string | null | undefined,
): { precio: string; ivaIncluido: boolean; notaFacturaA: string | null } {
  const moneda = monedaDelPais(pais);
  const tabla = PRECIOS[moneda];
  const i = Math.min(Math.max(Math.trunc(escalon) || 0, 0), tabla.agencias.length - 1);
  const numero = clave === 'equipos' ? tabla.equipos : tabla.agencias[i];
  const esArgentina = moneda === 'ARS';
  return {
    precio: `$${numero} ${moneda}`,
    ivaIncluido: esArgentina,
    notaFacturaA: esArgentina ? `Costo neto con factura A: $${netoFacturaA(numero)}` : null,
  };
}

export interface PlanDeEquipoEnCatalogo {
  clave: 'equipos' | 'agencias' | 'corporativo';
  nombre: string;
  descripcion: string;
  /** Badge sobre la card (sólo Agencias), como el "Popular" del catálogo individual. */
  badge?: string;
  /** "Todo lo incluido en X, más:" — la herencia, como en el catálogo individual. */
  hereda: string | null;
  incluye: string[];
}

export const CATALOGO_DE_PLANES_DE_EQUIPO: PlanDeEquipoEnCatalogo[] = [
  {
    clave: 'equipos',
    nombre: 'Equipos',
    descripcion: 'Para equipos de marketing y agencias en etapa inicial.',
    hereda: null,
    incluye: [
      'Hasta 3 usuarios',
      'Hasta 10 marcas con espacio de trabajo propio',
      'Kits de marca y estilos compartidos',
      'Trabajo colaborativo con todo el equipo',
      'Permisos y límites de consumo por usuario',
      'Avatares y productos ilimitados por marca',
    ],
  },
  {
    clave: 'agencias',
    nombre: 'Agencias',
    descripcion: 'Para agencias que producen contenido para múltiples marcas.',
    badge: 'Recomendado para agencias',
    hereda: 'Todo lo incluido en Equipos, más:',
    incluye: [
      'Hasta 50 usuarios y marcas ilimitadas',
      'Permisos por proyecto',
      'Reportes de consumo por marca',
      'Acceso a la API',
      'Integración personalizada al flujo de trabajo',
      'Procesamiento prioritario',
      'Account manager dedicado',
    ],
  },
  {
    clave: 'corporativo',
    nombre: 'Corporativo',
    descripcion: 'Para grandes marcas, holdings y redes de agencias.',
    hereda: 'Todo lo incluido en Agencias, más:',
    incluye: [
      'Usuarios ilimitados',
      'SSO y registro de actividad',
      'Contrato y acuerdo de tratamiento de datos',
      'Indemnización de propiedad intelectual',
      'Acuerdo de nivel de servicio (SLA)',
      'Integraciones a medida',
      'Orden de compra y facturación a medida',
    ],
  },
];

/** Precio de Corporativo: no hay cifra, se arma una propuesta. */
export const PRECIO_CORPORATIVO = {
  precio: 'A medida',
  nota: 'Armamos una propuesta según tus marcas, usuarios y volumen de producción. Contrato anual.',
  creditos: 'Créditos a medida',
  creditosDetalle: 'El volumen mensual que necesite tu operación.',
};

/** Lo que traen los tres planes, una vez arriba (como la fila de beneficios del catálogo). */
export function incluidoEnTodosLosPlanesDeEquipo(pais: string | null | undefined): string[] {
  return [
    'Sin costo por usuario',
    'Créditos compartidos',
    'Control de permisos',
    'Capacitación en vivo',
    'Licencia comercial',
    // "Factura A o B" es AFIP: sólo se promete en Argentina, como en la landing.
    ...(paisFlags(pais).isArgentina ? ['Factura A o B'] : []),
  ];
}

export const SOPORTE_PLANES_DE_EQUIPO = {
  titulo: 'Soporte dedicado por WhatsApp y correo electrónico',
  detalle: 'Atención directa del equipo de Atlas, incluida en todos los planes para equipos y agencias.',
};

export const COSTO_POR_CREDITO =
  '1 crédito por imagen Full HD · 2 por imagen 2K · 3 por imagen 4K · 2 por segundo de video';

/** La tarjeta del catálogo que corresponde al plan actual, para marcarla como "Tu plan". */
export function claveDelPlanDeEquipo(plan?: string | null): PlanDeEquipoEnCatalogo['clave'] | null {
  if (!esPlanDeEquipo(plan)) return null;
  const nombre = nombreDelPlanDeEquipo(plan);
  if (nombre === 'Equipos') return 'equipos';
  if (nombre === 'Agencias') return 'agencias';
  return 'corporativo';
}
